import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-599ceb1e"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "map_box" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", {
  id: "canvasContainer",
  class: "canvas_container"
}, null, -1))
const _hoisted_3 = [
  _hoisted_2
]

import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';

import { CesiumEntityType } from '@/interface/map';
import { SatelliteProperties } from '@/modules/SatelliteProperties';
import useCommonStore from '@/store/storeCommon';
import useMapStore from '@/store/storeMap';


export default /*#__PURE__*/_defineComponent({
  __name: 'Map',
  setup(__props) {

const {
  changeCoordinate,
  changeSatelliteName,
  changeCurrentTrackingSat,
  satelliteTleData,
  satelliteInfo,
} = useMapStore();
const { isMobile } = storeToRefs(useCommonStore());

const viewer = ref();
let onTickEvent: Cesium.Event.RemoveCallback | null = null;
let orbitEntity: CesiumEntityType | null = null;

const isFlyingTo = ref<boolean>(false);
const satelliteName = ref<string>('');

onMounted(() => {
  drawMap();
});

watch(satelliteTleData, () => {
  if (satelliteTleData.tle !== '') {
    getSatellite();
  }
});

/**
 * 위성 데이터를 가져와 그립니다.
 */
const getSatellite = (): void => {

  // 화면 상에 보이는 위성 정보 박스의 위성 이름을 세팅합니다.
  changeSatelliteName('CONTEC - OREUM SAT');

  // 위성 데이터 가지고 오는 로직 추가 예정
  let satelliteProperties = null;
  try {
    satelliteName.value = satelliteTleData.tle.split('\n')[0];

    satelliteProperties = new SatelliteProperties(
      satelliteName.value,
      satelliteTleData.tle,
    );

    let satelliteColor = Cesium.Color.ALICEBLUE;
    //Cesium 위성 그리기
    let satellite = {
      id: satelliteName.value,
      position: satelliteProperties.sampledPosition,
      billboard: new Cesium.BillboardGraphics({
        image: require('@/assets/images/ContecSat2.png'),
        color: satelliteColor,
        scale: isMobile.value ? 0.07 : 0.1,
        eyeOffset: new Cesium.Cartesian3(0.0, 0.0, 0.0),
      }),
      label: new Cesium.LabelGraphics({
        horizontalOrigin: 1,
        outlineColor: new Cesium.Color(7, 18, 255, 100),
        fillColor: satelliteColor,
        outlineWidth: 10,
        pixelOffset: new Cesium.Cartesian2(17, -5),
        style: Cesium.LabelStyle.FILL,
      }),
    };
    //cesium 위성 궤도 그리기
    orbitEntity = {
      id: satelliteName.value + 'orbit',
      position: satelliteProperties.sampledPositionInertial,
      path: new Cesium.PathGraphics({
        leadTime: satelliteProperties.orbit.orbitalPeriod * 60,
        trailTime: satelliteProperties.orbit.orbitalPeriod * 40,
        material: new Cesium.Color(13, 255, 17, 100),
        resolution: 30,
        width: 2,
      }),
    };

    // 기존 위성이 존재할지 삭제하고 새로운 TLE의 위성을 생성합니다.
    if (viewer.value.entities.values.length > 0
      && viewer.value.entities.getById(satelliteName.value)) {
      viewer.value.entities.removeAll();
    }

    // 위성 및 위성 궤도 Entity 추가
    viewer.value.entities.add(satellite);
    viewer.value.entities.add(orbitEntity);

    const satEntity = viewer.value.entities.getById(satelliteName.value);


    const satEntityPos = satEntity.position.getValue(
      viewer.value.clock.currentTime,
    );

    // setView 함수에 사용할 수 있는 좌표로 변환
    const currentPositionCartographic = Cesium.Cartographic.fromCartesian(satEntityPos);
    const x = Cesium.Math.toDegrees(currentPositionCartographic.longitude);
    const y = Cesium.Math.toDegrees(currentPositionCartographic.latitude);

    // 초기 화면 지구본의 카메라를 세팅
    viewer.value.camera.setView({ destination: Cesium.Cartesian3.fromDegrees(x - 10, y - 10, 25000000.0) });

    // 실시간 좌표 구해와서 위성 정보 박스의 좌표 값 최신화
    viewer.value.clock.onTick.addEventListener(() => {
      const entityPos = satEntity.position.getValue(
        viewer.value.clock.currentTime,
      );

      const pos = Cesium.Cartographic.fromCartesian(entityPos);

      const lon = (pos.longitude / Math.PI) * 180;
      const lat = (pos.latitude / Math.PI) * 180;

      changeCoordinate({
        lat: lat.toFixed(2),
        lon: lon.toFixed(2),
      });
    });

  } catch (e) {
    console.log(e);
  }
};


/**
 * 현재 보고 있는 View를 변경합니다.
 * isCurrentTrackingSat이 True일 경우 위성을 따라갑니다.
 * isCurrentTrackingSat이 False일 경우 3D 지구본을 보여줍니다.
 */
const changeCurrentView = async (): Promise<void> => {
  changeCurrentTrackingSat();
  const entity = viewer.value.entities.getById(satelliteName.value);
  if (satelliteInfo.isCurrentTrackingSat) {
    await setTrackingSatellite();
  } else {
    if (onTickEvent) onTickEvent();
    const satEntity = viewer.value.entities.getById(satelliteName.value);

    const satEntityPos = satEntity.position.getValue(
      viewer.value.clock.currentTime,
    );
    const currentPositionCartographic = Cesium.Cartographic.fromCartesian(satEntityPos);
    const x = Cesium.Math.toDegrees(currentPositionCartographic.longitude);
    const y = Cesium.Math.toDegrees(currentPositionCartographic.latitude);

    isFlyingTo.value = true;
    viewer.value.camera.flyHome(0.5);
    viewer.value.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(x - 10, y - 10, 25000000.0),
      duration: 0.7,
    });
    setTimeout(() => {
      isFlyingTo.value = false;
    }, 700);
    setTimeout(() => {
      entity.billboard.scale = isMobile.value ? 0.07 : 0.1;
    }, 400);
    setTimeout(() => {
      viewer.value.entities.remove(orbitEntity);
      viewer.value.entities.add(orbitEntity);
    }, 500);
  }
};

/**
 * 위성을 따라가는 화면으로 세팅합니다.
 */
const setTrackingSatellite = async (): Promise<void> => {
  const satEntity = viewer.value.entities.getById(satelliteName.value);
  const satOrbitEntity = viewer.value.entities.getById(`${satelliteName.value}orbit`);
  viewer.value.entities.remove(satOrbitEntity);
  isFlyingTo.value = true;
  await viewer.value.scene.camera.flyTo({
    destination: satEntity.position.getValue(
      viewer.value.clock.currentTime,
    ),
    orientation: new Cesium.HeadingPitchRange(-1.0, -0.6, 1000000.0),
    duration: 0.5,
  });
  setTimeout(() => {
    isFlyingTo.value = false;
  }, 500);
  setTimeout(() => {
    satEntity.billboard.scale = isMobile.value ? 0.17 : 0.22;
    onTickEvent = viewer.value.clock.onTick.addEventListener(() => {
      viewer.value.camera.lookAt(
        viewer.value.entities.values[0].position.getValue(
          viewer.value.clock.currentTime,
        ),
        new Cesium.HeadingPitchRange(-1.0, -0.6, 1000000.0),
      );
    });
  }, 400);
};

/**
 * 지도 데이터를 가져와 그립니다.
 */
const drawMap = (): void => {
  Cesium.Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI0MzdjOTkwZi0yZDFlLTRhNzMtYjJkZC02N2ZmMjNmZjMwZTMiLCJpZCI6MjA2NzMxLCJpYXQiOjE3MTIyOTM4OTV9.z8R3-bngosbBAARA8B9j__oJIR9YYtymi4JMz6ZYvlU';
  viewer.value = new Cesium.Viewer('canvasContainer', {
    homeButton: false,
    navigationHelpButton: false,
    fullscreenButton: false,
    geocoder: false,
    baseLayerPicker: false,
    shouldAnimate: true,
    timeline: false,
    animation: false,
    sceneMode: 3,
    sceneModePicker: false,
  });
  viewer.value._cesiumWidget._creditContainer.style.display = 'none';
  viewer.value.selectedEntity = undefined;

  viewer.value.infoBox.frame.removeAttribute('sandbox');
  viewer.value.infoBox.frame.src = 'about:blank';

  viewer.value.screenSpaceEventHandler.setInputAction(() => {
    if (!isFlyingTo.value) changeCurrentView();
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  viewer.value.scene.screenSpaceCameraController.minimumZoomDistance = 5000000;
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, _hoisted_3))
}
}

})